import { useState } from "react";
import type { FormEvent } from "react";
import { Navigate } from "react-router-dom";

import { ApiError, changePassword } from "../api/client";
import { useAuth } from "../auth/auth-context";
import { RequiredMark } from "../ui/required-mark";

/**
 * Choosing your own password — and the wall a one-time password stops at.
 *
 * ── Two arrivals, one screen ────────────────────────────────────────────────
 *
 * **Made to be here**, because an operator issued the password and
 * `mustChangePassword` is set: every other route bounces here, and the API
 * refuses everything else regardless. The screen says so, plainly, rather than
 * letting somebody wonder why the app will not open.
 *
 * **Chose to be here**, from the account menu, because they simply want a
 * different password. Same form, same rules, one less sentence.
 *
 * ── It signs you out, and that is deliberate ────────────────────────────────
 *
 * The server ends every session on a change — including this one — because a
 * password change is often made precisely because somebody else may have the
 * old one, and leaving a live refresh token behind would make the change
 * cosmetic. So this lands on the sign-in screen with a note, rather than
 * pretending the session survived.
 */
export function ChangePasswordPage(): React.JSX.Element {
  const { user, status, signOut } = useAuth();

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (status === "signed-out") return <Navigate to="/login" replace />;

  const forced = user?.mustChangePassword === true;

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);

    // A typing aid, not a rule — the server never hears about it.
    if (next !== confirm) {
      setError("Those two passwords are not the same.");
      return;
    }

    setSubmitting(true);
    try {
      await changePassword(current, next);

      /**
       * **A full page load, deliberately — the router cannot win this one.**
       *
       * Two gates fight over where a just-signed-out visitor goes, and both are
       * right on their own. Sign out first and `RequireAuth` sends them to the
       * product chooser before any navigation runs. Navigate to `/login` first
       * and `LoginPage` sees a session that has not been cleared yet, decides
       * they are already signed in, and bounces them to `/` — which lands on
       * the gate anyway. Both orders end on `/products`, with no sign that the
       * password was changed at all.
       *
       * `location.replace` steps outside the argument: the session is dropped,
       * the page is thrown away, and the browser arrives at `/login` with
       * nothing mounted to redirect it. `?changed=1` carries the message,
       * because router state does not survive a real navigation.
       */
      await signOut();
      window.location.replace("/login?changed=1");
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not reach the server.");
      setSubmitting(false);
    }
  }

  return (
    <main className="sheet-wide">
      <h1>{forced ? "Choose your own password" : "Change your password"}</h1>

      {forced ? (
        <p>
          The password you signed in with was set for you by whoever created this clinic, so they
          know it. Choose one only you know and DentaCore will open. Nothing else works until you
          do.
        </p>
      ) : (
        <p>
          Once you save it, every device signed in with the old password is signed out &mdash;
          including this one.
        </p>
      )}

      <form onSubmit={(event) => void handleSubmit(event)} noValidate>
        <label htmlFor="current-password">
          {forced ? "The password you were given" : "Your current password"}
          <RequiredMark />
        </label>
        <input
          id="current-password"
          type="password"
          autoComplete="current-password"
          required
          value={current}
          onChange={(event) => setCurrent(event.target.value)}
          data-testid="current-password"
        />

        <label htmlFor="new-password">
          New password
          <RequiredMark />
        </label>
        <input
          id="new-password"
          type="password"
          autoComplete="new-password"
          required
          minLength={12}
          value={next}
          onChange={(event) => setNext(event.target.value)}
          data-testid="new-password"
        />

        <label htmlFor="confirm-password">
          New password again
          <RequiredMark />
        </label>
        <input
          id="confirm-password"
          type="password"
          autoComplete="new-password"
          required
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
          data-testid="confirm-password"
        />

        <p className="quiet">At least 12 characters. Length is what matters, not symbols.</p>

        {error !== null && (
          <p role="alert" data-testid="change-password-error">
            {error}
          </p>
        )}

        <button type="submit" disabled={submitting} data-testid="change-password-submit">
          {submitting ? "Saving…" : "Save and sign in again"}
        </button>
      </form>

      {/*
        **No way out when it is forced.** Offering "later" would make the block
        a suggestion, and the API would refuse every screen they reached anyway
        — a link to a wall is worse than no link. Signing out stays available
        from here for somebody handed the wrong password entirely.
      */}
      {forced && (
        <p className="quiet">
          Wrong account?{" "}
          <button
            type="button"
            className="linklike"
            onClick={() => {
              // No navigation of its own: `RequireAuth` sends a signed-out
              // visitor to the chooser, which is the right place for somebody
              // who has decided this is not their account at all.
              void signOut();
            }}
            data-testid="change-password-signout"
          >
            Sign out
          </button>
        </p>
      )}
    </main>
  );
}
